import { ref, computed, watch } from 'vue'
import { format } from 'date-fns'
import { defineStore } from 'pinia'
import _slugify from 'slugify'
import { useI18n } from 'vue-i18n'
import { useNow } from '@/composables/useNow'
import { useTimify } from '@/composables/useTimify'
import { useTypesense } from '@/composables/useTypesense'
import { useDeviceStore } from '@/stores/device'
import { useNotification } from '@/composables/useNotification'
import { useCustomer } from '@/composables/useCustomer'

const appointmentColors = {
  default: { color: '#3788d8' },
  influencer: { color: '#7B55E6' },
  employee: { color: '#F08C34' },
  investor: { color: '#3983DC' },
  noShow: { color: '#999999' },
  'Abholung / Service': { color: '#313E97' },
  Beratung: { color: '#A9D0D5' },
  'Kostenloser Sehtest': { color: '#48C5B5' },
  Sehtest: { color: '#398075' },
  Führerscheinsehtest: { color: '#8A94E9' },
  Reklamation: { color: '#EC5B56' },
  'Store Admin 25 Min': {
    backgroundColor: '#E6E6E6',
    borderColor: '#E6E6E6',
    textColor: '#676767'
  },
  'Store Admin 30 Min': {
    backgroundColor: '#E6E6E6',
    borderColor: '#E6E6E6',
    textColor: '#676767'
  },
  'Store Admin 60 Min': {
    backgroundColor: '#E6E6E6',
    borderColor: '#E6E6E6',
    textColor: '#676767'
  },
  'Eyetest Walk-In': { color: '#96C3FF' },
  'Kostenloser Sehtest CH': { color: '#48C5B5' },
  'Remote Sehtest': { color: '#EF4782' },
  'ACUITY EYE TEST (EU)': {
    backgroundColor: '#E6E6E6',
    borderColor: '#E6E6E6',
    textColor: '#676767'
  },
  'ACUITY EYETEST (CH)': {
    backgroundColor: '#E6E6E6',
    borderColor: '#E6E6E6',
    textColor: '#676767'
  },
  'ACUITY STILBERATUNG': { color: '#ccb0be' },
  'Mounting': {
    backgroundColor: '#E6E6E6',
    borderColor: '#E6E6E6',
    textColor: '#676767'
  },
}

const serviceKeys = [
  'abholung_service',
  'beratung',
  'eyetest_walk_in',
  'kostenloser_eyetest_walk_in',
  'fuehrerscheinsehtest',
  'kostenloser_sehtest',
  'kostenloser_sehtest_ch',
  'kostenloser_sehtest_mit_anschliessender_beratung',
  'kostenloser_sehtest_mit_anschliessender_beratung_ch',
  'kontaktlinsen_anpassung',
  'mounting',
  'reklamation',
  'sehtest',
  'sehtest_mit_anschliessender_beratung',
  'sehtest_fuer_2',
  'store_admin_25_min',
  'store_admin_30_min',
  'store_admin_60_min'
]

export const useAppointmentsStore = defineStore('appointments', () => {

  const { t } = useI18n()
  const now = useNow()
  const deviceStore = useDeviceStore()
  const timify = useTimify()
  const notification = useNotification()
  const typesense = useTypesense()
  const { loadCustomersByEmailBulk } = useCustomer()
  const enabled = ref(false)
  const fetched = ref(false)
  // Set true by setMarket right before it hard-reloads the page on a store switch, so the
  // currentCompanyId watcher below skips a refresh whose fetches the reload would abort anyway.
  const reloading = ref(false)
  const companies = ref([])
  const appointments = ref([])
  const services = ref([])
  const resources = ref([])
  const modifyDialogOpen = ref(false)
  const entryDialogOpen = ref(false)
  const selectedAppointmentId = ref(null)
  const storeData = ref()
  let companiesRequest = null

  const employeeUnwatch = watch(() => deviceStore.settings.currentEmployee, async (value) => {
    if (value?.id) {
      try {
        companiesRequest = timify.companies()
        companies.value = await companiesRequest
        employeeUnwatch()
      } catch {
        //
      }
    }
  }, { immediate: true })

  const selectedAppointment = computed(() => {
    return viuAppointments.value?.find((appointment) => appointment.id === selectedAppointmentId.value)
  })

  const selectedAppointmentDateTimeModel = computed(() => {
    if (!selectedAppointment.value) {
      return undefined
    }
    return format(selectedAppointment.value.start, "yyyy-MM-dd'T'HH:mm")
  })

  watch(selectedAppointmentId, (value) => {
    if (value) {
      modifyDialogOpen.value = true
    }
  })
  
  watch(modifyDialogOpen, (value) => {
    if (!value) {
      selectedAppointmentId.value = null
    }
  })

  const currentStore = computed(() => {
    return deviceStore.settings.warehouse
  })

  const currentCompany = computed(() => {
    return companies.value?.find(company => {
      return company.externalId?.toLowerCase() === currentStore.value?.toLowerCase()
    })
  })

  const currentCompanyId = computed(() => {
    return currentCompany.value?.id
  })

  let updateRequest
  const updatingAppointments = ref(false)

  async function updateAppointments(delay = 500) {
    try {
      await new Promise(resolve => setTimeout(resolve, delay))
      updatingAppointments.value = true
      const request = timify.appointments(currentCompanyId.value)
      updateRequest = request
      const response = await request
      if (request === updateRequest) {
        const customers = response?.data?.flatMap(appointment => {
          return appointment.singleCustomers.map(customer => customer.email)
        })
        const uniqueCustomers = [...new Set(customers)]
        const loadedCustomers = await loadCustomersByEmailBulk(uniqueCustomers, ['tags'], {
          customers: ['email', 'tags'],
          tags: ['name']
        })
        const customerByEmail = loadedCustomers.reduce((acc, customer) => {
          return {
            ...acc,
            [customer.email]: customer
          }
        }, {})
        appointments.value = (response?.data || []).map((appointment) => {
          return {
            ...appointment,
            clCustomer: customerByEmail[appointment.singleCustomers[0]?.email] || null
          }
        })
        fetched.value = true
      }
    } catch {
      fetched.value = false
    } finally {
      updatingAppointments.value = false
    }
  }

  async function updateServices() {
    try {
      const response = await timify.services(currentCompanyId.value)
      services.value = response?.data || []
    } catch {
      //
    }
  }

  async function updateResources() {
    try {
      const response = await timify.resources(currentCompanyId.value)
      resources.value = response?.data || []
    } catch {
      //
    }
  }

  async function fetchStoreData() {
    storeData.value = await typesense
      .client()
      .collections(import.meta.env.VITE_TYPESENSE_STORES_COLLECTION)
      .documents()
      .search({
        q: currentStore.value,
        query_by: 'storeId',
        include_fields: 'openingHours'
      })
  }

  const networkError = ref(false)
  const networkErrorDisplayDuration = 3000

  watch(networkError, (value) => {
    if (value) {
      notification.showNotification({
        message: t('appointments.errors.network'),
        variant: 'danger'
      })
    }
  })

  function displayNetworkError() {
    networkError.value = true

    setTimeout(() => {
      networkError.value = false
    }, networkErrorDisplayDuration)
  }

  async function getCompanyById(companyId) {
    await companiesRequest
    return companies.value.find(company => company.id === companyId)
  }

  watch(currentCompanyId, async (companyId) => {
    // A store switch changes the settings (via setMarket -> $patch) and then reloads the page.
    // That reactive change fires this watcher, but the reload aborts the in-flight fetches ~1s
    // later (NetworkError / Failed to fetch). Skip the doomed refresh entirely.
    if (reloading.value) return
    if (companyId) {
      enabled.value = true
      appointments.value = []
      resources.value = []
      services.value = []
      await Promise.all([
        updateAppointments(0),
        updateServices(),
        updateResources(),
        fetchStoreData()
      ])
    } else {
      enabled.value = false
    }
  }, { immediate: true })

  const viuAppointments = computed(() => {
    return appointments.value
      .filter((appointment) => appointment.type === 'booking')
      .map((appointment) => {
        return {
          ...appointment,
          serviceKey: slugify(appointment.service?.name || appointment.title || '')
        }
      })
      .map((appointment) => {
        try {
          const { bookings } = appointment
          // we have multiple identical bookings, one for each resource I guess
          const [booking] = bookings
          const { interval } = booking
          const noShow = appointment.tags.some(tag => tag.externalId === 'no_show')
          const start = new Date(interval.begin)
          const normalColors = pickAppointmentColors(appointment, appointmentColors, noShow)
          // past appointments use the same color at 30% opacity blended with white
          const colors = start < new Date() ? lightenColors(normalColors) : normalColors
          return {
            ...appointment,
            start,
            end: new Date(interval.end),
            viuTitle: appointmentTitle(appointment),
            noShow,
            colors,
            style: colorsAsStyle(colors),
            normalStyle: colorsAsStyle(normalColors)
          }
        } catch (error) {
          console.log(appointment)
        }
      })
      .filter(Boolean)
  })

  const upcomingViuAppointments = computed(() => {
    return viuAppointments.value.filter((appointment) => {
      const start = new Date(appointment.start)
      start.setMinutes(start.getMinutes() + 10)
      return start >= now.value
    })
  })

  // fix manually added appointments
  function appointmentTitleFixByService(serviceKey, fallback = 'default') {
    // check if appointment.serviceKey exists in serviceKeys
    if (serviceKeys.includes(serviceKey)) {
      return t(`appointments.services.${serviceKey}`, fallback)
    } else {
      return fallback
    }
  }

  function appointmentTitle(appointment) {
      const [customer] = appointment.singleCustomers
      const employee = appointment.resources?.find((resource) => Boolean(resource.externalId))
      const employeeNamePieces = employee?.name.split(' ')
      const employeeFirstName = employeeNamePieces?.[0]
      const employeeLastName = employeeNamePieces?.[employeeNamePieces.length - 1]
      const employeeInitials = [
        Array.from(employeeFirstName || '')[0],
        Array.from(employeeLastName || '')[0]
      ]
        .filter(Boolean)
        .join('')
      const customerLastNameInitial = Array.from(customer?.lastName || '')[0]
      const customerName = [
        customer?.firstName,
        customerLastNameInitial ? `${customerLastNameInitial}.` : ''
      ]
        .filter(Boolean)
        .join(' ')
      let title
      if (!customerName) {
        const byServiceKey = appointmentTitleFixByService(appointment.serviceKey, appointment.title)
        title = [
          byServiceKey,
          employeeInitials ? `(${employeeInitials})` : ''
        ]
          .filter(Boolean)
      } else {
        title = [customerName, employeeInitials ? `(${employeeInitials})` : '']
          .filter(Boolean)
      }
    
      return title
  }

  return {
    enabled,
    fetched,
    reloading,
    appointments,
    viuAppointments,
    upcomingViuAppointments,
    companies,
    services,
    resources,
    storeData,
    currentCompany,
    currentCompanyId,
    selectedAppointmentId,
    selectedAppointment,
    selectedAppointmentDateTimeModel,
    modifyDialogOpen,
    entryDialogOpen,
    updatingAppointments,
    updateAppointments,
    displayNetworkError,
    getCompanyById,
    appointmentTitleFixByService
  }
})

function slugify(name, options = {}) {
  return _slugify(name, {
    lower: true,
    remove: /[./\\()]/,
    locale: 'de',
    ...options
  }).replace(/-/g, '_')
}

function lightenColor(hex, opacity = 0.4) {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  const lr = Math.round(r * (1 - opacity) + 255 * opacity).toString(16).padStart(2, '0')
  const lg = Math.round(g * (1 - opacity) + 255 * opacity).toString(16).padStart(2, '0')
  const lb = Math.round(b * (1 - opacity) + 255 * opacity).toString(16).padStart(2, '0')
  return `#${lr}${lg}${lb}`
}

function lightenColors(colors, opacity = 0.4) {
  if (colors.color) {
    return { color: lightenColor(colors.color, opacity) }
  }
  return {
    backgroundColor: lightenColor(colors.backgroundColor, opacity),
    borderColor: lightenColor(colors.borderColor, opacity),
    textColor: lightenColor(colors.textColor, opacity)
  }
}

function pickAppointmentColors(appointment, colorPalette, noShow) {
  if (noShow) {
    return colorPalette['noShow']
  }
  // Remote Sehtest takes priority over tag-based colors
  if (appointment.title.includes('Remote')) {
    return colorPalette['Remote Sehtest']
  }
  if (appointment.clCustomer?.tags?.some(tag => tag.name.includes('influencer'))) {
    return colorPalette['influencer']
  }
  if (appointment.clCustomer?.tags?.some(tag => tag.name.includes('employee'))) {
    return colorPalette['employee']
  }
  if (appointment.clCustomer?.tags?.some(tag => tag.name.includes('investor'))) {
    return colorPalette['investor']
  }
  return colorPalette[appointment.title] || colorPalette['default']
}

function colorsAsStyle(colors) {
  return {
    backgroundColor: colors.backgroundColor || colors.color,
    color: colors.textColor || '#ffffff'
  }
}
